import Popover from "@/components/Popover";
import { RenderMessage } from "@/components/RichText";
import { useAuthStore } from "@/store/authStore";
import useChatStore from "@/store/chatStore";
import { CHAT_ROLE, ChatMessageDto, SocketMessageDto } from "@/types";
import { cn } from "@/utils";
import { formatDateChat } from "@/utils/formatDate";
import React, { useMemo } from "react";
import { GoKebabHorizontal } from "react-icons/go";
import Attachment from "../../components/Attachment";
import { UserAvatar } from "../../components/UserAvatar";

interface ApiMessageProps {
  message: ChatMessageDto;
}
export const ApiMessage = ({ message }: ApiMessageProps) => {
  const { user } = useAuthStore();

  // check if the message sender is the current user
  const isSender = useMemo(() => {
    return message.sender_id === user?.id;
  }, [message, user]);

  return (
    <Message
      messageId={message.id}
      message={message.message}
      isSender={isSender}
      image={message.sender?.image || ""}
      timeStamp={message.created_at}
      stickerPath={message.sticker_path}
      name={message.sender?.full_name || message.sender?.email || "退職者"}
      files={message.files_attachment}
      editedAt={
        message.created_at !== message.updated_at
          ? message.updated_at
          : undefined
      }
      deletedAt={message.deleted_at}
      senderId={message.sender_id}
      isSystem={message.is_system}
    />
  );
};

interface SocketMessageProps {
  message: SocketMessageDto;
}

export const SocketMessage = ({ message }: SocketMessageProps) => {
  const { user } = useAuthStore();

  // check if the message sender is the current user
  const isSender = useMemo(() => {
    return message.senderId === user?.id;
  }, [message, user]);

  return (
    <Message
      messageId={message.messageId}
      message={message.message}
      isSender={isSender}
      image={message.userImage}
      timeStamp={message.timestamp}
      stickerPath={message.stickerPath}
      name={message.senderName}
      files={message.files}
      editedAt={message.updated_at}
      senderId={message.senderId}
    />
  );
};

interface Props {
  messageId: number;
  message?: string;
  files?: { url: string; type: string; name: string }[];
  isSender: boolean;
  image?: string;
  timeStamp?: string;
  name?: string;
  stickerPath?: string;
  editedAt?: string;
  deletedAt?: string;
  senderId?: number;
  isSystem?: boolean;
}

const Message: React.FC<Props> = ({
  messageId,
  message,
  files,
  isSender,
  image,
  timeStamp,
  name,
  stickerPath,
  editedAt,
  deletedAt,
  // senderId,
  isSystem,
}) => {
  // if no sender_id return message as system message
  if (isSystem) {
    return (
      <div className="m-4 text-xs text-light_secondary wrap-break-word text-center">
        {message}
      </div>
    );
  }

  // if chat role is viewer, hide message actions
  const chatRole = useChatStore((state) => state.chatRole);

  return (
    <div
      className={cn(
        "flex items-start gap-4 p-4 text-sm max-w-[90%]",
        isSender && "justify-end ml-auto"
      )}
    >
      {/* Menu actions, only display for sender  */}
      {isSender && !deletedAt && chatRole === CHAT_ROLE.INCHARGE && (
        <MessageActions messageId={messageId} messageText={message} />
      )}

      {/* Avatar user  */}
      {/* Hide avatar if sender is current user */}
      {!isSender && (
        <UserAvatar
          url={image}
          width={32}
          height={32}
          containerClassName="size-8"
        />
      )}

      {/* Message content  */}
      <div className={cn("text-light_primary min-w-0", deletedAt && "italic")}>
        {/* User name + message timestamp */}
        <div
          className={cn(
            "flex items-center gap-2 text-sm mb-2",
            isSender && "justify-end"
          )}
        >
          {!isSender && (
            <div className="leading-5.5 line-clamp-3 wrap-break-word">
              {name}
            </div>
          )}
          {timeStamp && (
            <div className="text-xs text-grey-600 leading-4.5 shrink-0">
              {formatDateChat(timeStamp)}
            </div>
          )}
        </div>

        {/* Message text  */}
        {message && <ContentMessage text={message} isSender={isSender} />}

        {/* Message files  */}
        {!!files &&
          (!deletedAt ? (
            <Attachment initialAttachments={files} isSender={isSender} />
          ) : (
            <ContentMessage text="添付資料が削除されました。" isSender={isSender} />
          ))}

        {/* Message sticker  */}
        {!!stickerPath && !deletedAt && (
          <img
            src={`${stickerPath}?t=v3`} // cache buster, to avoid browser caching when sticker is updated
            alt="sticker"
            className="max-w-full"
          />
        )}

        {/* Edited message info */}
        {!!editedAt && !deletedAt && (
          <div
            className={cn(
              "text-light_secondary text-xs",
              isSender && "text-right"
            )}
          >
            (編集)
          </div>
        )}
      </div>
    </div>
  );
};

interface ContentMessageProps {
  text: string;
  isSender: boolean;
  className?: string;
}
const ContentMessage = ({ text, isSender, className }: ContentMessageProps) => {
  return (
    <RenderMessage
      className={cn(
        "p-3 rounded-lg w-fit whitespace-pre-wrap wrap-break-word max-w-full",
        !isSender
          ? "bg-grey-200  text-light_primary"
          : "bg-secondary_lighter text-charleston_green ml-auto",
        className
      )}
      html={text}
      // shouldSanitize={false} // TODO: Sanitize to prevent XSS attacks to server
    ></RenderMessage>
  );
};

interface MessageActionsProps {
  messageId: number;
  messageText?: string;
}
const MessageActions = ({ messageId, messageText }: MessageActionsProps) => {
  const setEditingMessage = useChatStore((state) => state.setEditingMessage);

  // handle edit message text
  const handleEditMessage = () => {
    if (messageText)
      setEditingMessage({
        messageId,
        originalContent: messageText,
        isDeleted: false,
      });
  };

  // handle delete message
  const handleDeleteMessage = () => {
    // implement delete message logic here
    setEditingMessage({
      messageId,
      originalContent: messageText || "",
      isDeleted: true,
    });
  };

  return (
    <Popover
      trigger={<GoKebabHorizontal />}
      align="end"
      side="bottom"
      triggerClassName="mt-6"
    >
      <div className="rounded-lg shadow-light_dropdown bg-dark_primary flex flex-col text-sm">
        {!!messageText && (
          <button
            type="button"
            className="border-b px-4 py-1 hover:bg-grey-200 transition-colors"
            onClick={handleEditMessage}
          >
            編集
          </button>
        )}
        <button
          type="button"
          className="px-4 py-1 text-error_main hover:bg-grey-200 transition-colors"
          onClick={handleDeleteMessage}
        >
          削除
        </button>
      </div>
    </Popover>
  );
};
